import React, { useEffect, useRef, useState } from 'react'
import LoadingIcon from '../../../../../../components/LoadingIcon'

import { CurrencyList } from '../../../../Plans/Positions/Edit/types'

import HoverTooltip from '../../../../../../components/HoverTooltip'

const MONTHS = [
    'январь',
    'февраль',
    'март',
    'апрель',
    'май',
    'июнь',
    'июль',
    'август',
    'сентябрь',
    'октябрь',
    'ноябрь',
    'декабрь',
]

const formatMonthYear = (dateString: string | null): string => {
    if (!dateString) return '-'

    const [year, month] = dateString.split('-')
    const monthIndex = Number(month) - 1

    if (!year || monthIndex < 0 || monthIndex > 11) return '-'

    return `${MONTHS[monthIndex]} ${year}`
}

type PlanInfo = {
    guid: string
    registration_number: number
    plan_type: string | null
}

type PlanPositionSelectItem = {
    guid: string
    ordinal_number: number
    contract_subject: string | null
    plan_guid: string
    organization_guid: string
    type_object_purchase_position: string | null
    currency_code: string | null
    currency_digital_code: number | null
    currency_name: string | null
    is_electronic: boolean | null
    is_purchase_smb: boolean | null
    purchase_method_name: string | null
    maximum_contract_price: number | string | null
    exchange_rate: number | null
    exchange_rate_date: string | null
    maximum_contract_price_rub: number | null

    contract_end_date: string | null
    purchase_planned_date: string | null
    purchase_period_year: number | null
    purchase_period_month: number | null
}

type SelectedPlanPositionResult = {
    plan: PlanInfo
    position: PlanPositionSelectItem
}

type Props = {
    open: boolean
    theme: string
    year: number | null
    onClose: () => void
    onSelect: (item: SelectedPlanPositionResult) => void
    currency_data: CurrencyList[]
}

const ContractPlanPositionSelectPopup: React.FC<Props> = (props) => {
    const [loading, setLoading] = useState(false)
    const [searchLoading, setSearchLoading] = useState(false)

    const [plan, setPlan] = useState<PlanInfo | null>(null)
    const [items, setItems] = useState<PlanPositionSelectItem[]>([])
    const [selectedGuid, setSelectedGuid] = useState<string | null>(null)

    const [searchValue, setSearchValue] = useState('')

    const [isInitialSearchReady, setIsInitialSearchReady] = useState(false)

    const isFirstSearchEffect = useRef(true)

    const loadPlanByYear = async (
        yearValue: number
    ): Promise<PlanInfo | null> => {
        const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/backend/v2/plan_by_year?year=${yearValue}`,
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )

        if (!response.ok) {
            throw new Error('Ошибка загрузки плана по году')
        }

        const data = await response.json()

        return {
            guid: data.guid,
            registration_number: data.registration_number,
            plan_type: data.plan_type ?? null,
        }
    }

    const loadPlanPositions = async (
        regNumber: number,
        textQuery = ''
    ): Promise<PlanPositionSelectItem[]> => {
        const params = new URLSearchParams({
            reg_number: String(regNumber),
            page: '1',
            size: '30',
        })

        if (textQuery.trim()) {
            params.append('text_query', textQuery.trim())
        }

        const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/backend/v2/plan_positions_with_approval_and_user_and_text_query/?${params.toString()}`,
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )

        if (!response.ok) {
            throw new Error('Ошибка загрузки позиций плана')
        }

        const data = await response.json()
        return Array.isArray(data) ? data : []
    }

    const loadInitialData = async () => {
        if (!props.year) return

        setLoading(true)
        setIsInitialSearchReady(false)

        try {
            const loadedPlan = await loadPlanByYear(props.year)
            setPlan(loadedPlan)

            if (!loadedPlan) {
                setItems([])
                return
            }

            const loadedItems = await loadPlanPositions(
                loadedPlan.registration_number,
                ''
            )
            setItems(loadedItems)
        } catch (e) {
            console.error(e)
            setPlan(null)
            setItems([])
        } finally {
            setLoading(false)
            setIsInitialSearchReady(true)
        }
    }

    const handleSearch = async (text: string) => {
        if (!props.year || !plan) return

        setSearchLoading(true)
        try {
            const loadedItems = await loadPlanPositions(
                plan.registration_number,
                text
            )
            setItems(loadedItems)
            setSelectedGuid(null)
        } catch (e) {
            console.error(e)
            setItems([])
        } finally {
            setSearchLoading(false)
        }
    }

    useEffect(() => {
        if (!props.open) return

        isFirstSearchEffect.current = true

        setSelectedGuid(null)
        setSearchValue('')
        loadInitialData()
    }, [props.open, props.year])

    useEffect(() => {
        if (!props.open || !plan || !isInitialSearchReady) return

        if (isFirstSearchEffect.current) {
            isFirstSearchEffect.current = false
            return
        }

        const timer = setTimeout(() => {
            handleSearch(searchValue)
        }, 2000)

        return () => clearTimeout(timer)
    }, [searchValue, props.open, plan, isInitialSearchReady])

    if (!props.open) return null

    const selectedItem =
        items.find((item) => item.guid === selectedGuid) ?? null

    return (
        <div className="fixed top-0 left-0 z-50 w-full h-full bg-[#00000050] flex justify-center items-center">
            <div
                className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-[1000px] h-[600px] flex flex-col gap-4"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex flex-col gap-1">
                    <div className="text-[16px] text-[#181818] dark:text-[#FFFFFF]">
                        Выбор позиции плана закупки
                    </div>
                    {plan ? (
                        <div className="text-[12.8px] text-[#6A6A6A] dark:text-[#8A8A8A]">
                            План закупки товаров, работ, услуг №{' '}
                            {plan.registration_number}
                        </div>
                    ) : (
                        ''
                    )}
                </div>

                {!props.year ? (
                    <div className="text-[12.8px] text-[#181818] dark:text-[#FFFFFF]">
                        Не удалось определить год плана.
                    </div>
                ) : loading ? (
                    <div className="flex justify-center items-center h-[460px]">
                        <LoadingIcon />
                    </div>
                ) : (
                    <>
                        <div className=" flex flex-row gap-3 items-end">
                            <div className="flex flex-col flex-1">
                                <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                    Поиск позиции плана
                                </label>
                                <div className="relative flex flex-row w-full items-center justify-end">
                                    <input
                                        type="text"
                                        className="form-input user-input-placeholder w-full"
                                        value={searchValue}
                                        onChange={(e) =>
                                            setSearchValue(e.target.value)
                                        }
                                        placeholder="Введите номер позиции / предмет договора / цену договора"
                                        style={{
                                            colorScheme:
                                                props.theme === 'dark'
                                                    ? 'dark'
                                                    : 'light',
                                        }}
                                    />
                                    <div className="absolute flex w-6 h-6 mr-1 rounded-full items-center justify-center">
                                        <svg
                                            className="w-4 h-4"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 24 24"
                                            id="loupe"
                                        >
                                            <path
                                                d="M21.71,20.29,18,16.61A9,9,0,1,0,16.61,18l3.68,3.68a1,1,0,0,0,1.42,0A1,1,0,0,0,21.71,20.29ZM11,18a7,7,0,1,1,7-7A7,7,0,0,1,11,18Z"
                                                fill={
                                                    props.theme == 'dark'
                                                        ? '#FFFFFF'
                                                        : '#181818'
                                                }
                                            />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col w-full h-[450px] items-center justify-center">
                            <div className="flex flex-row w-full gap-1 p-1 py-3 px-2 items-center justify-center rounded-t-md text-[10px] text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d2d5d8] dark:bg-[#333033]">
                                {/* <div className="flex w-5 items-center justify-center ">
                                    {searchLoading && (
                                        <div className="flex items-center justify-center text-center text-[#6A6A6A]">
                                            <svg
                                                className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                            >
                                                <circle
                                                    className="opacity-25"
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                ></circle>
                                                <path
                                                    className="opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                                ></path>
                                            </svg>
                                        </div>
                                    )}
                                </div> */}
                                <div className="flex w-10 items-center justify-center text-center">
                                    № в ЕИС
                                </div>
                                <div className="flex flex-1 items-center justify-center text-center">
                                    Предмет договора
                                </div>
                                <div className="flex w-32 items-center justify-center text-center">
                                    НМЦД
                                </div>
                                <div className="flex w-24 items-center justify-center text-center">
                                    Дата (период) размещения
                                </div>
                                <div className="flex w-24 items-center justify-center text-center">
                                    Срок исполнения договора
                                </div>
                                <div className="flex w-44 items-center justify-center text-center">
                                    Способ закупки
                                </div>
                            </div>
                            <div className="flex flex-col w-full h-[340px] gap-1 p-1 items-start justify-start rounded-b-md overflow-y-auto custom-scrollbar bg-[#e4e6e8] dark:bg-[#1D1C1E] text-[11px]">
                                {searchLoading ? (
                                    <div className="flex justify-center items-center h-[340px] w-full">
                                        <LoadingIcon />
                                    </div>
                                ) : items.length ? (
                                    items.map((item) => (
                                        <button
                                            key={item.guid}
                                            className={`flex flex-col w-full h-fit gap-1 p-1 items-center justify-center rounded-md cursor-pointer text-[#181818] dark:text-[#FFFFFF] ${
                                                selectedGuid === item.guid
                                                    ? 'bg-[#ffffff] dark:bg-[#373538] hover:bg-[#ffffff] hover:dark:bg-[#373538]'
                                                    : 'bg-[#f6f6f6] dark:bg-[#2a282b] hover:bg-[#ffffff] hover:dark:bg-[#373538]'
                                            }`}
                                            onClick={() =>
                                                setSelectedGuid(item.guid)
                                            }
                                            onDoubleClick={() => {
                                                if (plan) {
                                                    props.onSelect({
                                                        plan,
                                                        position: item,
                                                    })
                                                }
                                            }}
                                        >
                                            <div className="flex flex-row w-full gap-1 items-center justify-center">
                                                {/* <div className="flex w-5 items-center justify-center ">
                                                    <input
                                                        type="checkbox"
                                                        checked={
                                                            selectedGuid ===
                                                            item.guid
                                                        }
                                                        onChange={() => {
                                                            if (
                                                                item.guid !=
                                                                null
                                                            ) {
                                                                setSelectedGuid(
                                                                    String(
                                                                        item.guid
                                                                    )
                                                                )
                                                            }
                                                        }}
                                                    />
                                                </div> */}
                                                <div className="flex w-10 items-center justify-center text-[#6A6A6A] dark:text-[#8A8A8A]">
                                                    {item.ordinal_number}
                                                </div>
                                                <div className="flex flex-1 w-full p-1 items-start justify-start text-left text-[#181818] dark:text-[#FFFFFF] ">
                                                    <HoverTooltip
                                                        tooltipText={
                                                            item.contract_subject ||
                                                            '-'
                                                        }
                                                    >
                                                        <p className="fourrow">
                                                            {item.contract_subject ||
                                                                '-'}
                                                        </p>
                                                    </HoverTooltip>
                                                </div>
                                                <div className="flex w-32 p-1 items-center justify-end text-right text-[#181818] dark:text-[#FFFFFF] ">
                                                    {item.currency_digital_code
                                                        ? item.currency_digital_code ===
                                                          643
                                                            ? ''
                                                            : props.currency_data.find(
                                                                  (c) =>
                                                                      c.num_code ===
                                                                      String(
                                                                          item.currency_digital_code
                                                                      )
                                                              )?.char_sign ||
                                                              '?'
                                                        : ''}{' '}
                                                    {Number(
                                                        item.maximum_contract_price
                                                    ).toLocaleString('ru-RU', {
                                                        minimumFractionDigits: 2,
                                                    })}
                                                </div>
                                                <div className="flex w-24 p-1 items-center justify-center text-center text-[#181818] dark:text-[#FFFFFF] ">
                                                    {item.purchase_planned_date ===
                                                    null ? (
                                                        item.purchase_period_month ? (
                                                            <>
                                                                {MONTHS[
                                                                    Number(
                                                                        item.purchase_period_month
                                                                    ) - 1
                                                                ] + ' '}
                                                                {
                                                                    item.purchase_period_year
                                                                }
                                                            </>
                                                        ) : (
                                                            '-'
                                                        )
                                                    ) : (
                                                        item.purchase_planned_date
                                                            ?.split('-')
                                                            .reverse()
                                                            .join('-')
                                                            .replace(
                                                                /[\-]/g,
                                                                '.'
                                                            )
                                                    )}
                                                </div>
                                                <div className="flex w-24 p-1 items-center justify-center text-center text-[#181818] dark:text-[#FFFFFF] ">
                                                    {formatMonthYear(
                                                        item.contract_end_date
                                                    )}
                                                </div>
                                                <div className="flex w-44 items-center p-1 justify-center text-[#181818] dark:text-[#FFFFFF] ">
                                                    <HoverTooltip
                                                        tooltipText={
                                                            item.purchase_method_name ||
                                                            '-'
                                                        }
                                                    >
                                                        <p className="fourrow">
                                                            {item.purchase_method_name ||
                                                                '-'}
                                                        </p>
                                                    </HoverTooltip>
                                                </div>
                                            </div>
                                        </button>
                                    ))
                                ) : (
                                    <div className="flex w-full h-[50px] items-center justify-center text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                        Позиции плана не найдены
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}

                <div className="flex justify-end gap-3">
                    <button
                        className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                        onClick={props.onClose}
                    >
                        Отмена
                    </button>
                    <button
                        className={`flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs 
                            ${!selectedItem || !plan ? 'text-[#6a6a6a] dark:text-[#8a8a8a] bg-[#ededed] dark:bg-[#494849]' : ' shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]'}
                         `}
                        disabled={!selectedItem || !plan}
                        onClick={() => {
                            if (selectedItem && plan) {
                                props.onSelect({
                                    plan,
                                    position: selectedItem,
                                })
                            }
                        }}
                    >
                        Выбрать
                    </button>
                </div>
            </div>
        </div>
    )
}

export default ContractPlanPositionSelectPopup
